import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { InterceptorUtil, RoutingService, USE_CLIENT_TOKEN } from '@spartacus/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ComponentService } from '../service/component.service';
import { ShimanoClientErrorHandlingService } from '../service/error/shimano-client-error-handling.service';
import { ShimanoUserErrorHandlingService } from '../service/error/shimano-user-error-handling.service';
import { StandardModalService } from '../service/modal/standard-modal.service';
import { LogoutService } from '../service/user/logout.service';

const OAUTH_ENDPOINT = '/authorizationserver/oauth/token';

@Injectable({
  providedIn: 'root',
})
export class ShimanoAuthErrorInterceptor implements HttpInterceptor {
  activeCartId: string;

  constructor(
    private userErrorHandlingService: ShimanoUserErrorHandlingService,
    private clientErrorHandlingService: ShimanoClientErrorHandlingService,
    private logoutService: LogoutService,
    protected routingService: RoutingService,
    private activeCartFacade: ActiveCartFacade,
    private modalService: StandardModalService,
    private componentService: ComponentService
  ) {
    this.activeCartFacade.getActiveCartId().subscribe((cartId) => {
      this.activeCartId = cartId;
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isClientTokenRequest = this.isClientTokenRequest(request);
    if (isClientTokenRequest) {
      request = InterceptorUtil.removeHeader(USE_CLIENT_TOKEN, request);
    }

    return next.handle(request).pipe(
      catchError((errResponse: any) => {
        if (errResponse instanceof HttpErrorResponse) {
          switch (errResponse.status) {
            case 401: // Unauthorized
              if (isClientTokenRequest) {
                if (this.isExpiredToken(errResponse)) {
                  return this.clientErrorHandlingService.handleExpiredClientToken(request, next);
                }
                // user token request
              } else {
                if (this.isExpiredToken(errResponse)) {
                  return this.userErrorHandlingService.handleExpiredUserToken(request, next);
                } else if (
                  // Refresh expired token
                  // Check that the OAUTH endpoint was called and the error is for refresh token is expired
                  errResponse.url.includes(OAUTH_ENDPOINT) &&
                  errResponse.error.error === 'invalid_token'
                ) {
                  this.userErrorHandlingService.handleExpiredRefreshToken();
                  return of();
                } else if (this.isExpiredBearerToken(errResponse)) {
                  this.userErrorHandlingService.handleExpiredRefreshToken('InvalidBearerTokenError');
                }
              }
              break;
            case 400: // Bad Request
              if (errResponse.url.includes(OAUTH_ENDPOINT) && errResponse.error.error === 'invalid_grant') {
                if (request.body.get('grant_type') === 'refresh_token') {
                  // refresh token fail, force user logout
                  this.logoutService.logout();
                }
              } else if (errResponse.url.includes('/anonymous/') || errResponse.url.includes('/cms/')) {
                if (errResponse.url.includes('?pageType=ProductPage')) {
                  console.log('Interceptor triggered: Product is not found (pageType=ProductPage) is triggered');
                  this.routingService.goByUrl('/product-not-found');
                }
                return of();
              } else if (this.isCartModificationPaymentError(errResponse)) {
                const ref = this.getPaymentRef(errResponse.error.errors);
                this.routingService.goByUrl(`/payment-confirmation/${this.activeCartId}?ref=${ref}`);
                this.componentService.loadPaymentConfirmation$.next(true);
                this.modalService.closeDialog('close');
              }
              break;
          }
        }
        return throwError(errResponse);
      })
    );
  }

  private isClientTokenRequest(request: HttpRequest<any>): boolean {
    const isRequestMapping = InterceptorUtil.getInterceptorParam(USE_CLIENT_TOKEN, request.headers);
    return Boolean(isRequestMapping);
  }

  private isExpiredToken(resp: HttpErrorResponse): boolean {
    const expiredTypes = ['InvalidTokenError'];
    const errors = resp?.error?.errors;
    if (Array.isArray(errors) && errors[0]?.type && expiredTypes.includes(errors[0].type)) {
      return true;
    }
    if (resp?.error?.error === 'invalid_token') {
      return true;
    }
    return false;
  }

  private isExpiredBearerToken(resp: HttpErrorResponse): boolean {
    const expiredTypes = ['InvalidBearerTokenError'];
    const errors = resp?.error?.errors;
    console.log('[isExpiredBearerToken]', { type: errors?.[0]?.type, error: resp?.error?.errors });
    if (Array.isArray(errors) && errors[0]?.type && expiredTypes.includes(errors[0].type)) {
      return true;
    }
    if (resp?.error?.error === 'invalid_token') {
      return true;
    }
    return false;
  }

  private isCartModificationPaymentError(resp: HttpErrorResponse): boolean {
    return resp.error.errors.some((err: any) => err.type === 'ShimanoCartModificationPaymentError');
  }

  private getPaymentRef(errors: any[]): string {
    const error: any = errors.find((err: any) => err.type === 'ShimanoCartModificationPaymentError');
    return error.message;
  }
}
